// The colgroup: the single home of the table's horizontal geometry. Every rendered column gets one <col> in
// header order; a data column's width is its saved width, else its configured initial width, raised to its
// content floor. While the table is narrower than its region, the spare width goes first to columns whose
// header or value shows truncated (spareWidths, view.js). The table lays out from these widths (table-layout:
// fixed), and is never narrower than the sum of the widths before any spare width.

import React from 'react'

import { useTable } from '../context'
import { DEFAULT_WIDTHS, DEFAULT_DENSITY } from '../config/defaults'
import { isResizable, renderedWidth, spareWidths } from '../view/view'
import { controlWidth } from './controlSize'

const isText = value => typeof value === 'string' && value.trim() !== ''

/** A resizable column's content floor (contentFloor.js), when one is measured. */
export const floorOf = (table, column) => {
  const floors = table.geometry && table.geometry.floors
  return isResizable(column) && floors ? floors[column.key] : undefined
}

/** The width a data column renders at before spare width: its saved or initial width, raised to its floor. */
export const columnWidth = (table, column) =>
  renderedWidth(column, table.state.view, floorOf(table, column))

const densityOf = ({ config, state }) =>
  (state && state.view && state.view.density) ||
  (config && config.defaultDensity) ||
  DEFAULT_DENSITY

/**
 * The rendered columns, in header order, each with its width before spare width; key is null for a control
 * column. A control column's width comes from its control's size (controlSize.js), so its focus ring fits
 * inside the cell.
 * @param {import('../types').TableContextValue} table
 * @returns {Array<{key: ?string, width: number}>}
 */
export const baseWidths = table => {
  const { config, layout } = table
  const density = densityOf(table)
  const out = []
  if (layout.selectable)
    out.push({ key: null, width: controlWidth('select', density) })
  if (layout.expandable)
    out.push({ key: null, width: controlWidth('expand', density) })
  layout.columns.forEach(column => {
    const width = columnWidth(table, column)
    out.push({
      key: column.key,
      width: typeof width === 'number' ? width : DEFAULT_WIDTHS.text,
    })
  })
  if (layout.hasActions)
    out.push({
      key: null,
      width: controlWidth('actions', density, {
        labelled: isText(config.rowActionsLabel),
      }),
    })
  return out
}

/** The table's minimum width: the sum of its widths before spare width, or the configured minimum when larger. */
export const tableMinWidth = table => {
  const sum = baseWidths(table).reduce((total, col) => total + col.width, 0)
  const configured = table.config.minTableWidth || 0
  return Math.max(sum, configured)
}

/**
 * Each data column's share of the spare width, keyed by column key: the region's content width (or the
 * configured minimum table width, when larger) less the widths before spare width. Empty until the region is
 * measured, and while the table is as wide as its region or wider.
 * @param {import('../types').TableContextValue} table
 * @returns {Object<string, number>}
 */
export const spareOf = table => {
  const geometry = table.geometry || {}
  const content = geometry.contentWidth || 0
  if (!(content > 0)) return {}
  const base = baseWidths(table)
  const sum = base.reduce((total, col) => total + col.width, 0)
  const spare = Math.max(content, table.config.minTableWidth || 0, sum) - sum
  if (!(spare >= 1)) return {}
  const naturals = geometry.naturals || {}
  const saved = (table.state.view && table.state.view.widths) || {}
  const byKey = new Map(
    table.layout.columns.map(column => [column.key, column])
  )
  return spareWidths(
    base
      .filter(col => col.key !== null && isResizable(byKey.get(col.key)))
      .map(col => ({
        key: col.key,
        width: col.width,
        natural: naturals[col.key],
        max: (byKey.get(col.key).size || {}).max,
        saved: Object.prototype.hasOwnProperty.call(saved, col.key),
      })),
    spare
  )
}

/**
 * The rendered columns, in header order, each with the width it renders at: its width before spare width
 * plus its share of the spare width; key is null for a control column.
 * @param {import('../types').TableContextValue} table
 * @returns {Array<{key: ?string, width: number, spare: number}>}
 */
export const columnWidths = table => {
  const spare = spareOf(table)
  return baseWidths(table).map(col => {
    const extra = (col.key !== null && spare[col.key]) || 0
    return { ...col, width: col.width + extra, spare: extra }
  })
}

/** The width a data column renders at, its share of the spare width included. */
export const shownWidth = (table, column) => {
  const width = columnWidth(table, column)
  if (typeof width !== 'number') return width
  return width + (spareOf(table)[column.key] || 0)
}

export const ColGroup = () => {
  const table = useTable()
  // the colgroup renders before every header and body cell, so the lock plan they read is this render's
  const { geometry, layout } = table
  const widths = columnWidths(table)
  if (geometry && typeof geometry.syncColumns === 'function')
    geometry.syncColumns(layout.columns, widths)
  // the geometry measures overflow without the spare width, which only ever fills the region
  if (geometry && typeof geometry.syncSpare === 'function')
    geometry.syncSpare(widths.reduce((total, col) => total + col.spare, 0))
  return (
    <colgroup>
      {widths.map((col, index) =>
        col.key === null ? (
          <col
            key={`control-${index}`}
            className="me-table-col-control"
            style={{ width: `${col.width}px` }}
          />
        ) : (
          <col
            key={col.key}
            data-column-key={col.key}
            style={{ width: `${col.width}px` }}
          />
        )
      )}
    </colgroup>
  )
}
